// Equal-tile photo grid: every tile the same square, the whole photo shown (never cropped), clean gutters.
// Columns are chosen per set; phones show at most 2. Optional lightbox for a closer look.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { abs } from "./vbShared";

export const PhotoGrid = ({ photos, columns = 3, onOpen, renderBadge, tileClass = "bg-black/40", testid = "photo-grid" }) => (
  <div className="grid gap-2 sm:gap-3 grid-cols-[repeat(var(--mc),minmax(0,1fr))] md:grid-cols-[repeat(var(--c),minmax(0,1fr))]" style={{ "--c": columns, "--mc": Math.min(columns, 2) }} data-testid={testid} data-columns={columns}>
    {photos.map((p, i) => (
      <div key={p.photo_id || p.url} className={`relative aspect-square rounded-xl overflow-hidden ring-1 ring-white/10 ${tileClass}`} data-testid={`${testid}-tile-${i}`}>
        <button type="button" onClick={() => onOpen?.(i)} className={`absolute inset-0 block ${onOpen ? "cursor-zoom-in" : "cursor-default"}`} aria-label={p.caption || `Photo ${i + 1}`}>
          <img src={abs(p.url)} alt={p.caption || ""} loading="lazy" className="h-full w-full object-contain" />
        </button>
        {renderBadge?.(p, i)}
      </div>
    ))}
  </div>
);

export const PhotoLightbox = ({ photos, index, onClose, onIndex }) => {
  const sx = useRef(null);
  const [i, setI] = useState(index);
  useEffect(() => setI(index), [index]);
  const go = (d) => { const n = (i + d + photos.length) % photos.length; setI(n); onIndex?.(n); };
  useEffect(() => {
    const k = (e) => {
      if (e.key === "Escape") { e.stopImmediatePropagation(); onClose(); }
      if (e.key === "ArrowRight") { e.stopImmediatePropagation(); go(1); }
      if (e.key === "ArrowLeft") { e.stopImmediatePropagation(); go(-1); }
    };
    window.addEventListener("keydown", k, true);
    return () => window.removeEventListener("keydown", k, true);
  });
  const p = photos[i];
  if (!p) return null;
  return createPortal(
    <div className="fixed inset-0 z-[90] bg-black/92 backdrop-blur-sm flex flex-col" onClick={onClose} onPointerDown={(e) => { sx.current = e.clientX; }} onPointerUp={(e) => { const dx = e.clientX - (sx.current ?? e.clientX); if (Math.abs(dx) > 60) { e.stopPropagation(); go(dx < 0 ? 1 : -1); } }} data-testid="photo-lightbox">
      <div className="flex items-center justify-between px-4 h-14 text-white/70 text-sm font-bold">
        <span className="tabular-nums">{i + 1} / {photos.length}</span>
        <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-white/10" aria-label="Close" data-testid="photo-lightbox-close"><X className="h-5 w-5" /></button>
      </div>
      <div className="relative flex-1 min-h-0 flex items-center justify-center px-2 sm:px-16" onClick={(e) => e.stopPropagation()}>
        <img src={abs(p.url)} alt={p.caption || ""} className="max-h-full max-w-full object-contain select-none" draggable={false} />
        {photos.length > 1 && (
          <>
            <button type="button" onClick={() => go(-1)} className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="Previous photo" data-testid="photo-lightbox-prev"><ChevronLeft className="h-6 w-6" /></button>
            <button type="button" onClick={() => go(1)} className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="Next photo" data-testid="photo-lightbox-next"><ChevronRight className="h-6 w-6" /></button>
          </>
        )}
      </div>
      <p className="m-0 min-h-[3.5rem] px-6 py-4 text-center text-sm text-white/75">{p.caption}</p>
    </div>,
    document.body
  );
};
